import React, { useState, useEffect, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import SearchBar from "../../common/SearchBar";
import FilterButton from "../../common/FilterButton";
import FilterPanel from "../../common/FilterPanel";
import { Dropdown } from "antd";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import ActiveFilterBar from "../../common/ActiveFilterBar";
import { Drawer as MuiDrawer } from "@mui/material";
import Pagination from "../../common/TablePagination";
import Checkbox from "../../common/Checkbox";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { ReactComponent as TagIcon } from "../../../Assets/Icons/discount-tag.svg";
import { ReactComponent as LocationIcon } from "../../../Assets/Icons/MapIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import {
    getAllMerchants,
    getIncludedMerchants,
    getExcludedMerchants,
    updateMerchantVisibility,
} from "../../../services/fundraiserService";
import { getAllCategories } from "../../../services/offerService";
import debounce from "lodash.debounce";

const MerchantVisibility = () => {
    const dispatch = useDispatch();
    const loading = useSelector((state) => state?.fundraiserReducer?.loading);
    const merchantCategories = useSelector((state) => state?.offerReducer?.categories);

    const [activeTab, setActiveTab] = useState("all"); // 'all' or 'excluded'
    const [searchQuery, setSearchQuery] = useState("");
    const [data, setData] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [initialSelectionMap, setInitialSelectionMap] = useState({});
    const [pendingChanges, setPendingChanges] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const limit = 10;

    // Filter Data Configuration
    const filterData = useMemo(() => [
        {
            id: "category",
            title: "Category",
            options: merchantCategories || [],
            optionLabelKey: "title",
            optionValueKey: "_id",
            defaultValue: [],
            multiSelect: false
        }
    ], [merchantCategories]);

    // Initialize selections
    const createInitialState = (filters) => {
        return filters.reduce((acc, filter) => {
            acc[filter.id] = filter.multiSelect ? [] : filter.defaultValue;
            return acc;
        }, {});
    };

    const [selections, setSelections] = useState(createInitialState(filterData));
    const [selectedCategory, setSelectedCategory] = useState(null);

    const activeFilterChips = useMemo(() => {
        const chips = [];
        if (selectedCategory) {
            const filter = filterData.find(f => f.id === "category");
            if (filter) {
                const displayLabel = filter.options.find(opt => opt[filter.optionValueKey] === selectedCategory)?.[filter.optionLabelKey] || selectedCategory;
                chips.push({ id: "category", value: selectedCategory, label: `${displayLabel}` });
            }
        }
        return chips;
    }, [selectedCategory, filterData]);

    const activeCount = activeFilterChips.length;

    const handleRemoveFilter = (targetChip) => {
        const filter = filterData.find((f) => f.id === targetChip.id);
        if (!filter) return;

        let newValue;
        if (filter.multiSelect) {
            newValue = selections[targetChip.id].filter((v) => v !== targetChip.value);
        } else {
            newValue = null;
        }

        const newSelections = { ...selections, [targetChip.id]: newValue };
        setSelections(newSelections);
        setSelectedCategory(newValue);
    };

    const columns = [
        { name: "No." },
        { name: "Merchant Name" },
        { name: "Offer Category" },
        { name: "No. of Offers", align: "center" },
        { name: "No. of Locations", align: "center" },
        { name: "Action", align: "center" },
    ];

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const toggleVisibility = (merchantId, currentStatus) => {
        const newIsSelected = !currentStatus;

        // Update local table data immediately for snappy UI
        setData((prevData) =>
            prevData.map((row) =>
                row._id === merchantId ? { ...row, isSelected: newIsSelected } : row
            )
        );

        // Track pending changes compared to the original state
        setPendingChanges((prev) => {
            const original = initialSelectionMap[merchantId];

            // If the new state matches the original, remove from pending changes
            if (original === undefined || original === newIsSelected) {
                const { [merchantId]: _removed, ...rest } = prev;
                return rest;
            }

            // Otherwise record the new desired state
            return {
                ...prev,
                [merchantId]: { isSelected: newIsSelected },
            };
        });
    };

    const handleSaveChanges = async () => {
        const changes = Object.entries(pendingChanges);
        if (!changes.length) return;

        try {
            setIsSaving(true);
            // Group changes by selection status to perform bulk updates if supported,
            // or at least handle the loading state locally as a single block.
            const toInclude = changes.filter(([_, change]) => change.isSelected === true).map(([id]) => id);
            const toExclude = changes.filter(([_, change]) => change.isSelected === false).map(([id]) => id);

            if (toInclude.length > 0) {
                await dispatch(updateMerchantVisibility({
                    merchantIds: toInclude,
                    isSelected: true
                })).unwrap();
            }

            if (toExclude.length > 0) {
                await dispatch(updateMerchantVisibility({
                    merchantIds: toExclude,
                    isSelected: false
                })).unwrap();
            }

            // Check if we're including merchants from excluded tab before clearing data
            const isIncludingMerchants = activeTab === "excluded" && toInclude.length > 0;

            // Clear pending changes
            setPendingChanges({});

            // Switch to "All Merchants" tab if we were including merchants from excluded tab
            if (isIncludingMerchants) {
                setActiveTab("all");
                setCurrentPage(1);
            } else {
                // Refresh data for current tab
                await fetchMerchants(searchQuery);
            }
        } catch (error) {
            console.error("Error saving merchant visibility changes:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelChanges = () => {
        setPendingChanges({});
        setData((prevData) =>
            prevData.map((row) => ({
                ...row,
                isSelected: initialSelectionMap[row._id] !== undefined
                    ? initialSelectionMap[row._id]
                    : row.isSelected
            }))
        );
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        // Scroll to top of the main content area on page change
        const contentArea = document.getElementById("main-content-area");
        if (contentArea) {
            contentArea.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const fetchMerchants = async (search) => {
        try {
            const params = {
                page: 1, // Always fetch page 1
                limit: 1000, // Fetch all to ensure client-side sort works across the dataset
                search: search || null,
                categoryId: selectedCategory || null, // Sent to service
            };

            // Remove null values
            Object.keys(params).forEach((key) =>
                params[key] === null ? delete params[key] : {}
            );

            let response;
            if (activeTab === "all") {
                response = await dispatch(getIncludedMerchants(params)).unwrap();
            } else if (activeTab === "excluded") {
                response = await dispatch(getExcludedMerchants(params)).unwrap();
            }

            if (response?.success) {
                let incomingData = response?.data || [];

                // Sort by createdAt descending to show latest first
                incomingData = [...incomingData].sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                    return dateB - dateA;
                });

                // Client-side search filtering as fallback
                if (search) {
                    const q = search.toLowerCase().trim();
                    incomingData = incomingData.filter((row) => {
                        const bName = row.businessName || "";
                        const uName = row.user?.name || "";
                        const mName = row.merchant?.businessName || "";
                        const name = (bName || uName || mName || "").toLowerCase();
                        return name.includes(q);
                    });
                }

                // Client-side category filtering as fallback
                if (selectedCategory) {
                    incomingData = incomingData.filter((row) => {
                        const targetCatObj = (merchantCategories || []).find(c => c._id === selectedCategory);
                        const targetTitle = targetCatObj?.title?.toLowerCase()?.trim();

                        // Match against EXACTLY what is displayed in the table (row.category?.[0])
                        const displayedCategory = Array.isArray(row.category) ? row.category[0] : row.category;

                        if (!displayedCategory) return false;

                        const itemId = typeof displayedCategory === 'object' ? displayedCategory._id : displayedCategory;
                        const itemTitle = typeof displayedCategory === 'object' ? displayedCategory.title : null;

                        // 1. Check ID
                        if (itemId === selectedCategory) return true;

                        // 2. Check Title with fuzzy/plural logic (fallback)
                        if (targetTitle && itemTitle) {
                            const currentTitle = itemTitle.toLowerCase().trim();
                            if (currentTitle === targetTitle) return true;
                            if (currentTitle === `${targetTitle}s` || targetTitle === `${currentTitle}s`) return true;
                        }

                        return false;
                    });
                }

                setData(incomingData);

                // Recalculate totalPages for excluded tab since it's filtered client-side for now
                setTotalPages(Math.ceil(incomingData.length / limit) || 1);

                // Capture the initial selection state for change tracking
                const initialMap = {};
                (incomingData || []).forEach((row) => {
                    if (row?._id) {
                        // Logic: 
                        // 1. If isSelected is explicitly set in database, use it.
                        // 2. Otherwise (default case), use true only if they have approved offers.
                        const hasApprovedOffers = (row.approvedOfferCount || 0) > 0;
                        const isSelected = row?.hasOwnProperty('isSelected')
                            ? row.isSelected
                            : hasApprovedOffers;

                        initialMap[row._id] = isSelected;
                    }
                });
                setInitialSelectionMap(initialMap);
                // Removed setPendingChanges({}) to persist selections across pagination
            }
        } catch (error) {
            console.error("Error fetching merchants:", error);
            setData([]);
        }
    };

    useEffect(() => {
        dispatch(getAllCategories());
    }, [dispatch]);

    useEffect(() => {
        fetchMerchants(searchQuery);
    }, [activeTab, selectedCategory]);

    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                setCurrentPage(1);
                fetchMerchants(query);
            }, 500),
        [activeTab, selectedCategory]
    );

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        debouncedSearch(value);
    };

    const handleApply = () => {
        setIsFilterOpen(false);
        // Now selections.category is the ID because of optionValueKey: "_id"
        setSelectedCategory(selections.category || null);
        setCurrentPage(1);
    };

    const handleCancel = () => {
        setIsFilterOpen(false);
    };

    const handleReset = () => {
        setSelections(createInitialState(filterData));
        setSelectedCategory(null);
    };

    const handleSelectionChange = (filterId, value) => {
        setSelections((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    return (
        <div className="w-full h-full bg-white">
            {(loading || isSaving) && <Loader />}
            {/* Full-width Container */}
            <div className="bg-white px-2 py-4 md:p-2.5 h-full">

                {/* Sticky Header Section */}
                <div className="sticky top-[-20px] bg-white z-[30] -mx-2 px-2 pt-1">
                    {/* Header - Responsive Centering */}
                    <div className="mb-4 md:mb-4 flex flex-col items-center text-center px-4">
                        <h1 className="text-[22px] md:text-[28px] font-bold text-[#303030]">
                            Merchants <span className="text-[#00ADEF]">Visibility</span>
                        </h1>
                        <p className="text-[12px] md:text-[14px] text-[#8C8C8C] mt-2 max-w-[800px] leading-relaxed">
                            This section lets you control the Swoodle merchants. <br className="hidden md:block" />
                            All merchants are visible by default. Uncheck a merchant if you want to hide it from your supporting members.
                        </p>
                    </div>

                    {/* Tabs - Responsive Scaling */}
                    <div className="flex gap-2 md:gap-4 mb-2 md:mb-2 overflow-x-auto pb-1 px-3 md:px-0 no-scrollbar justify-start md:justify-start">
                        <button
                            onClick={() => handleTabChange("all")}
                            className={`flex-shrink-0 px-4 md:px-5 py-2 rounded-full text-[12px] md:text-[13px] font-semibold transition-all whitespace-nowrap ${activeTab === "all"
                                ? "bg-[#00ADEF] text-white shadow-md"
                                : "bg-white text-[#00ADEF] border border-[#00ADEF]"
                                } items-center flex gap-1`}
                        >
                            All Merchants
                        </button>
                        <button
                            onClick={() => handleTabChange("excluded")}
                            className={`flex-shrink-0 px-4 md:px-5 py-2 rounded-full text-[12px] md:text-[13px] font-semibold transition-all whitespace-nowrap ${activeTab === "excluded"
                                ? "bg-[#00ADEF] text-white shadow-md"
                                : "bg-white text-[#00ADEF] border border-[#00ADEF]"
                                }`}
                        >
                            Excluded Merchants
                        </button>
                    </div>

                    {/* Search and Filters - Responsive Alignment */}
                    <div className="flex flex-row justify-between items-center mb-4 md:mb-6 gap-3 md:gap-4 px-3 md:px-0">
                        <div className="flex-1 max-w-[400px]">
                            <SearchBar
                                parentDivClass="w-full"
                                divClassName="relative w-full"
                                inputClassName="w-full p-2.5 md:p-3 pr-10 text-[13px] md:text-[14px] border border-[#E0E0E0] rounded-[8px] outline-none focus:border-[#00ADEF] transition-colors"
                                iconClassName="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-[#00ADEF]"
                                placeholder="Search Merchant"
                                value={searchQuery}
                                handleChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <FilterButton
                                activeCount={activeCount}
                                isOpen={isFilterOpen}
                                onClick={() => setIsFilterOpen(true)}
                            />
                        </div>
                        <MuiDrawer
                            anchor="right"
                            open={isFilterOpen}
                            onClose={() => setIsFilterOpen(false)}
                            ModalProps={{ keepMounted: true }}
                            TransitionComponent={Slide}
                            SlideProps={{
                                appear: true,
                                timeout: { enter: 700, exit: 500 },
                                easing: {
                                    enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
                                    exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
                                },
                            }}
                            PaperProps={{
                                sx: {
                                    width: { xs: '100%', sm: '400px' },
                                    padding: '0',
                                    height: '100%'
                                }
                            }}
                        >
                            <FilterPanel
                                filterData={filterData}
                                selections={selections}
                                handleApply={handleApply}
                                handleCancel={handleCancel}
                                handleReset={handleReset}
                                handleSelectionChange={handleSelectionChange}
                                style={{ maxHeight: "unset" }}
                            />
                        </MuiDrawer>
                    </div>

                    <ActiveFilterBar
                        chips={activeFilterChips}
                        onRemove={handleRemoveFilter}
                        onClearAll={handleReset}
                    />

                    {/* Full-width Divider Line after Search (Mobile only) */}
                    <div className="md:hidden w-full border-b border-[#F0F0F0] mb-6"></div>
                </div>

                <div className="overflow-hidden">
                    {(() => {
                        const displayData = data;
                        return displayData.length > 0;
                    })() ? (
                        <>
                            {/* Mobile Card View */}
                            <div className="md:hidden flex flex-col gap-4 mb-24 pb-4 px-1">
                                {data.slice((currentPage - 1) * limit, currentPage * limit).map((row) => {
                                    const isSelected = pendingChanges[row._id] !== undefined
                                        ? pendingChanges[row._id].isSelected
                                        : initialSelectionMap[row._id];

                                    const hasApprovedOffers = (row.approvedOfferCount || 0) > 0;
                                    const isExcludedTab = activeTab === "excluded";
                                    const isCheckboxDisabled = false;

                                    const logoPath = row.user?.image || row.logo || row.merchant?.logo;
                                    const src = logoPath?.startsWith('http') ? logoPath : (process.env.REACT_APP_S3_URL + logoPath);

                                    return (
                                        <div
                                            key={row._id}
                                            className={`relative bg-white rounded-xl border-2 p-4 transition-all shadow-sm ${isSelected ? "border-[#00ADEF]" : "border-[#E0E0E0] opacity-80"}`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div
                                                    className={`mt-[-2px] ${isCheckboxDisabled ? "opacity-30 pointer-events-none cursor-not-allowed" : ""}`}
                                                    title={isCheckboxDisabled ? "Cannot include merchant without approved offers" : ""}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChecked={() => toggleVisibility(row._id, isSelected)}
                                                        checkClassName="cursor-pointer scale-[1.3]"
                                                    />
                                                </div>

                                                <div className="flex-1 min-w-0 pr-14">
                                                    <h3
                                                        className="text-[15px] font-bold text-[#303030] truncate mb-2"
                                                        title={row.businessName}
                                                    >
                                                        {row.businessName || "-"}
                                                    </h3>
                                                    <div className="inline-block bg-[#00ADEF] text-white text-[11px] font-medium px-3 py-1 rounded-[6px] mb-3">
                                                        {row.category?.[0]?.title || "-"}
                                                    </div>

                                                    {/* Internal Divider */}
                                                    <div className="w-full border-t border-[#F0F0F0] mb-3"></div>

                                                    <div className="flex items-center gap-1.5 text-[#8C8C8C] text-[12px] font-medium">
                                                        <div className="flex items-center gap-1 opacity-60 grayscale">
                                                            <TagIcon className="w-3.5 h-3.5" />
                                                            <span>{row.offerCount || 0} Offers</span>
                                                        </div>
                                                        <span className="opacity-40">•</span>
                                                        <div className="flex items-center gap-1 opacity-60 grayscale">
                                                            <LocationIcon className="w-3.5 h-3.5" />
                                                            <span>{row.locationCount || 0} Locations</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Logo on top right of the card */}
                                                <div className="absolute top-4 right-4 h-full">
                                                    <div className="sticky top-0">
                                                        {logoPath ? (
                                                            <img
                                                                src={src}
                                                                alt={row.businessName}
                                                                className="w-11 h-11 rounded-full object-cover border border-gray-100 shadow-sm"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div
                                                            className="w-11 h-11 rounded-full bg-[#F3F4F6] border border-gray-200 flex items-center justify-center text-[13px] font-bold text-[#00ADEF]"
                                                            style={{ display: logoPath ? 'none' : 'flex' }}
                                                        >
                                                            {row.businessName?.charAt(0) || "M"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <TableContainer className="hidden md:block" style={{ width: "100%", overflowX: "auto" }}>
                                <Table
                                    sx={{
                                        minWidth: "1000px",
                                        borderCollapse: "separate",
                                        borderSpacing: "0px 8px",
                                    }}
                                    aria-label="merchant visibility table"
                                >
                                    <TableHead className="bg-[#00ADEF]">
                                        <TableRow
                                            className="h-[50px]"
                                            sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
                                        >
                                            {columns.map((col, index) => (
                                                <TableCell
                                                    key={index}
                                                    className="!text-[#FFFFFF]"
                                                    align={col.align || "left"}
                                                    sx={{
                                                        pl: index === 0 ? "40px" : (index === 1 ? "20px" : "16px"),
                                                        pr: index === columns.length - 1 ? "40px" : "16px"
                                                    }}
                                                >
                                                    {col.name}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.slice((currentPage - 1) * limit, currentPage * limit).map((row, index) => {
                                            const isSelected = pendingChanges[row._id] !== undefined
                                                ? pendingChanges[row._id].isSelected
                                                : initialSelectionMap[row._id];

                                            const hasApprovedOffers = (row.approvedOfferCount || 0) > 0;
                                            const isExcludedTab = activeTab === "excluded";
                                            const isCheckboxDisabled = false;

                                            return (
                                                <TableRow
                                                    key={row._id}
                                                    className="h-[50px]"
                                                    sx={{
                                                        boxShadow: "0px 4px 4px 0px #00000040",
                                                        backgroundColor: (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                                                    }}
                                                >
                                                    <TableCell sx={{ pl: "40px" }} className="max-w-[50px]">{(currentPage - 1) * limit + index + 1}</TableCell>
                                                    <TableCell sx={{ pl: "20px" }} className="max-w-[200px] truncate">
                                                        <div className="flex items-center gap-3">
                                                            {/* Merchant logo - using S3 URL if it's a relative path */}
                                                            {(() => {
                                                                const logoPath = row.user?.image || row.logo || row.merchant?.logo;
                                                                if (!logoPath) return null;
                                                                const src = logoPath.startsWith('http') ? logoPath : (process.env.REACT_APP_S3_URL + logoPath);
                                                                return (
                                                                    <img
                                                                        src={src}
                                                                        alt={row.businessName}
                                                                        className="w-8 h-8 rounded-full object-cover"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }}
                                                                    />
                                                                );
                                                            })()}
                                                            <div
                                                                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0"
                                                                style={{ display: (row.user?.image || row.logo || row.merchant?.logo) ? 'none' : 'flex' }}
                                                            >
                                                                {row.businessName?.charAt(0) || "M"}
                                                            </div>
                                                            <span
                                                                className="truncate max-w-[180px]"
                                                                title={row.businessName}
                                                            >
                                                                {row.businessName || "-"}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="max-w-[150px] truncate">
                                                        {row.category?.[0]?.title || "-"}
                                                    </TableCell>
                                                    <TableCell className="max-w-[100px] truncate" align="center">
                                                        {row.offerCount || 0}
                                                    </TableCell>
                                                    <TableCell className="max-w-[100px] truncate" align="center">
                                                        {row.locationCount || 0}
                                                    </TableCell>
                                                    <TableCell sx={{ pr: "40px" }} className="max-w-[80px] truncate" align="center">
                                                        <div
                                                            className={`flex justify-center scale-90 ${isCheckboxDisabled ? "opacity-30 pointer-events-none cursor-not-allowed" : ""}`}
                                                            title={isCheckboxDisabled ? "Cannot include merchant without approved offers" : ""}
                                                        >
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChecked={() => toggleVisibility(row._id, isSelected)}
                                                                checkClassName="cursor-pointer"
                                                            />
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination - Hide for Excluded Merchants unless count > limit */}
                            {totalPages > 1 && (activeTab !== "excluded" || data.length > limit) && (
                                <Pagination
                                    totalPages={totalPages}
                                    handlePageChange={handlePageChange}
                                    currentPage={currentPage}
                                    previousLabel={<ChevronLeft className="w-4 h-4" />}
                                    nextLabel={<ChevronRight className="w-3 h-3" />}
                                />
                            )}

                            {/* Bottom floating Save Changes bar - Responsive Design */}
                            {Object.keys(pendingChanges).length > 0 && (() => {
                                const excludedCount = Object.values(pendingChanges).filter(c => c.isSelected === false).length;
                                const includedCount = Object.values(pendingChanges).filter(c => c.isSelected === true).length;

                                let label = "Changes";
                                if (includedCount > 0 && excludedCount === 0) label = "Included";
                                else if (excludedCount > 0 && includedCount === 0) label = "Excluded";

                                const countLabel = includedCount + excludedCount;

                                return (
                                    <div className="fixed left-1/2 -translate-x-1/2 bottom-10 z-[100] pointer-events-none">
                                        <div className="bg-white shadow-[0px_4px_25px_rgba(0,0,0,0.15)] rounded-2xl border border-[#E5E7EB] px-6 py-3 pointer-events-auto flex items-center justify-center gap-6 md:gap-8">
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center justify-center min-w-[32px] h-8 px-2 rounded-[6px] border border-[#00ADEF] text-[#303030] text-[14px] font-medium bg-white">
                                                    {countLabel}
                                                </div>
                                                <span className="text-[15px] font-medium text-[#303030] whitespace-nowrap">
                                                    {label}
                                                </span>
                                            </div>

                                            <div className="w-[1px] h-6 bg-[#E0E0E0] flex-shrink-0"></div>

                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={handleCancelChanges}
                                                    className="px-6 py-2 border border-[#00ADEF] text-[#00ADEF] text-[14px] font-medium rounded-lg hover:bg-gray-50 active:scale-[0.98] transition-all whitespace-nowrap"
                                                >
                                                    Cancel
                                                </button>

                                                <button
                                                    onClick={handleSaveChanges}
                                                    className="px-6 py-2 bg-[#00ADEF] text-white text-[14px] font-medium rounded-lg hover:bg-[#0095CC] active:scale-[0.98] transition-all shadow-md shadow-[#00ADEF20] whitespace-nowrap"
                                                >
                                                    Save Changes
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        /* Empty State - With Table Header Visible */
                        <>
                            <TableContainer className="hidden md:block" style={{ width: "100%", overflowX: "auto" }}>
                                <Table
                                    sx={{
                                        minWidth: "1000px",
                                        borderCollapse: "separate",
                                        borderSpacing: "0px 8px",
                                    }}
                                    aria-label="merchant visibility table"
                                >
                                    <TableHead className="bg-[#00ADEF]">
                                        <TableRow
                                            className="h-[50px]"
                                            sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
                                        >
                                            {columns.map((col, index) => (
                                                <TableCell
                                                    key={index}
                                                    className="!text-[#FFFFFF]"
                                                    align={col.align || "left"}
                                                    sx={{
                                                        pl: index === 0 ? "40px" : (index === 1 ? "20px" : "16px"),
                                                        pr: index === columns.length - 1 ? "40px" : "16px"
                                                    }}
                                                >
                                                    {col.name}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                </Table>
                            </TableContainer>
                            <div className="flex flex-col items-center justify-center py-20 px-4">
                                {/* Empty State Graphic - Skeleton Lines */}
                                <div className="mb-6 flex flex-col items-center gap-2">
                                    <svg width="158" height="106" viewBox="0 0 158 106" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="0.5" y="0.5" width="157" height="105" rx="9.5" fill="white" stroke="none" />
                                        {/* Top Line */}
                                        <rect x="25" y="25" width="40" height="8" rx="4" fill="#F0F0F0" />
                                        <rect x="75" y="25" width="60" height="8" rx="4" fill="#F0F0F0" />

                                        {/* Middle Line */}
                                        <rect x="25" y="45" width="20" height="8" rx="4" fill="#F0F0F0" />
                                        <rect x="55" y="45" width="80" height="8" rx="4" fill="#F0F0F0" />

                                        {/* Bottom Line */}
                                        <rect x="25" y="65" width="30" height="8" rx="4" fill="#F0F0F0" />
                                        <rect x="65" y="65" width="68" height="8" rx="4" fill="#F0F0F0" />
                                    </svg>
                                </div>

                                <h3 className="text-[18px] font-bold text-[#303030] mb-2">
                                    {activeTab === "excluded"
                                        ? "No merchants have been excluded yet."
                                        : "No merchants found."}
                                </h3>
                                <p className="text-[14px] text-[#8C8C8C] text-center max-w-[400px]">
                                    {activeTab === "excluded"
                                        ? "To exclude a merchant, uncheck it from the list of all merchants."
                                        : "Try adjusting your search or check back later."}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MerchantVisibility;
